import { useEffect, useMemo, useRef, useState } from "react";
import { CornerUpLeft, Loader2, Send, ArrowRight } from "lucide-react";
import { clearDraft, draftChanged, getDraft, setDraft } from "@/lib/writingDraft";
import { locateTarget } from "@/lib/targetSpan";
import { EDITOR_TEXT_CLASS, WritingEditor, editorHtmlFor, plainTextOf, sanitizeFormatting } from "@/components/WritingEditor";
import { annotateMarks } from "@/lib/formatAnnotate";

// THE FOCUSED COMPOSITION WORKSPACE — "WORKING ON THIS PART": the student works on the SAME object TWS teaches — the CURRENT FUNCTIONAL
// UNIT, whose span (one paragraph, several, part of one) is decided by the backend's semantic reading and only PROJECTED here (`span`); with no
// reading, the span is the CONTEXT the student's own arrangement gives (an introduction / conclusion of several paragraphs) — the local locus
// stays editable, the rest of the span is read-only context in the same working area. The two highlights (MAIN POINT / WORKING HERE) may sit
// anywhere in the displayed span. A guard keeps displayed span == authoritative span; a missing paragraph is REPORTED, never hidden.
// THE TARGET UNDER DISCUSSION: the DCO's verbatim focus for this turn is highlighted in the editor when it can be located with confidence
// (exactly once) — "this is the writing we are talking about", never "this is wrong". Uncertain → no highlight.
// TWO LAYERS, ONE SURFACE: the student's FORMATTING (bold / italic / underline / lists / quote — presentation only, never evidence) is edited
// in the same clean WritingEditor My Writing uses; the MAIN POINT / WORKING HERE marks are TWS's reading of the canonical words. They are
// never put inside contentEditable: a MIRROR under the editor lays out the editor's own sanitized HTML with identical metrics, and the marks
// are wrapped onto the mirror's text nodes by the shared canonical-offset mapping (lib/formatAnnotate) — so bold or a list never moves a
// highlight, and a mark that cannot be mapped safely is simply not drawn (the formatting stays). Marks are located in the PLAIN text only.
// ACTIONS: Save & read with TWS (primary — "I changed this; keep helping me") · Move on (secondary — "I'm finished with this for now;
// continue": the existing set-aside cascade decides what comes next) on ONE row; Go back (return to the larger writing view) stays quiet.
// The student is never trapped: Move on and Go back never wait on TWS's judgment or the requested revision.
// (Component name kept for wiring stability; the student never sees it.)
const NAV = "inline-flex items-center gap-1 text-[11.5px] text-stone-500 hover:text-[#8C3A2A] transition-colors disabled:opacity-40";
const PROSE = "px-4 py-3.5 text-[15px] leading-[1.75]";
const MARK = { working: "rounded-[3px] bg-amber-300/[0.45] [box-decoration-break:clone]", main: "rounded-[3px] bg-sky-300/[0.4] [box-decoration-break:clone]" };
const markClass = (kind, visible) => `${MARK[kind]} ${visible ? "text-stone-900" : "text-transparent"}`;
const validFormatting = (text, fmt) => (fmt && plainTextOf(fmt) === (text || "").trim() ? sanitizeFormatting(fmt) : "");

export function ParagraphFocus({ unit, location, view, busy = false, span = null, focusText = "", onBack, onMoveOn, onTurn, onSave, draftScope }) {
  const restored = draftChanged(getDraft(draftScope, unit.id));
  const [draft, setDraftState] = useState(() => (restored ? getDraft(draftScope, unit.id).text : (unit.text || "")));
  const [touched, setTouched] = useState(() => restored);
  // the formatting the editor OPENED with (the record's, when it re-renders the record's words; a restored draft has none) and its current state
  const [openedFmt, setOpenedFmt] = useState(() => (restored ? "" : validFormatting(unit.text, unit.formatting)));
  const [fmt, setFmt] = useState(openedFmt);
  const [liveHtml, setLiveHtml] = useState(() => editorHtmlFor(draft, openedFmt));   // the editor's whole sanitized surface — what the mirror lays out
  const [gen, setGen] = useState(0);                                                 // the editor reopens on the record's words when they change untouched
  const [leaving, setLeaving] = useState(false);
  const backRef = useRef(null);
  useEffect(() => {
    if (touched) return;
    const f = validFormatting(unit.text, unit.formatting);
    if ((unit.text || "") === draft && f === fmt) return;
    setDraftState(unit.text || ""); setOpenedFmt(f); setFmt(f); setLiveHtml(editorHtmlFor(unit.text || "", f)); setGen((g) => g + 1);
  }, [unit.text, unit.formatting, touched]);   // eslint-disable-line react-hooks/exhaustive-deps

  const pending = !!(view && view.pending) || busy;
  const textChanged = draft.trim() !== (unit.text || "").trim();
  const changed = textChanged || fmt !== openedFmt;
  const vm = (view && view.episode && view.episode.round1 && view.episode.round1.visual_map) || null;
  // TWO ATTENTION HIGHLIGHTS INSIDE THE WRITING — no panel beneath the editor. MAIN POINT = the closest controlling point (`refine_focus.
  // main_point_text`, only when it is the student's exact words; else omitted); WORKING HERE = the DCO's verbatim focus (the instructional
  // object). Overlap → the WORKING HERE treatment alone; the DB explains the relation. Shown only for a reading of the CURRENT words.
  const rf = (vm && vm.functional_map && vm.functional_map.refine_focus) || null;
  const mainPointText = (rf && rf.main_point_text) || "";
  const change = ({ text, formatting, html }) => {
    setDraftState(text); setFmt(formatting || ""); setLiveHtml(html || editorHtmlFor(text, formatting)); setTouched(true);
    setDraft(draftScope, unit.id, text, unit.text || "");
  };

  // THE WORKING SPAN: the unit's paragraphs in reading order; the composing unit is the editor, the others read-only prose
  const parts = useMemo(() => (span && Array.isArray(span.parts) && span.parts.length ? span.parts : [{ id: unit.id, text: unit.text || "" }]), [span, unit.id, unit.text]);
  const spanOk = !span || span.ok !== false;
  const editorAt = Math.max(0, parts.findIndex((p) => p.id === unit.id));

  // THE TARGET UNDER DISCUSSION (confidence-gated) — located anywhere in the displayed context span: the editor (the local locus) or a read-only
  // paragraph of the same part. MAIN POINT likewise: the controlling point of the part may sit in another paragraph than the one being edited.
  const target = useMemo(() => (pending ? null : locateTarget(draft, focusText)), [draft, focusText, pending]);
  const mainPoint = useMemo(() => {
    if (pending) return null;
    const m = locateTarget(draft, mainPointText);
    if (!m) return null;
    if (target && m.start < target.end && target.start < m.end) return null;                     // overlap: one clear treatment, never stacked colour
    return m;
  }, [draft, mainPointText, pending, target]);
  const marks = useMemo(() => [target && { ...target, kind: "working" }, mainPoint && { ...mainPoint, kind: "main" }].filter(Boolean).sort((a, b) => a.start - b.start), [target, mainPoint]);
  const contextMarks = useMemo(() => {
    const out = {};
    if (pending) return out;
    parts.forEach((p, i) => {
      if (i === editorAt) return;
      const t = locateTarget(p.text, focusText);
      const m0 = locateTarget(p.text, mainPointText);
      const m = m0 && !(t && m0.start < t.end && t.start < m0.end) ? m0 : null;
      const ms = [t && { ...t, kind: "working" }, m && { ...m, kind: "main" }].filter(Boolean).sort((a, b) => a.start - b.start);
      if (ms.length) out[p.id] = ms;
    });
    return out;
  }, [parts, editorAt, focusText, mainPointText, pending]);
  const anyWorking = !!target || Object.values(contextMarks).some((ms) => ms.some((m) => m.kind === "working"));
  const anyMain = !!mainPoint || Object.values(contextMarks).some((ms) => ms.some((m) => m.kind === "main"));
  // PLAIN PATH (unchanged): marks sliced out of the plain text
  const renderMarked = (text, ms, suffix, visible) => ms.reduce((acc, mk, k) => {
    const prevEnd = k === 0 ? 0 : ms[k - 1].end;
    acc.push(<span key={`t${k}`}>{text.slice(prevEnd, mk.start)}</span>);
    acc.push(<mark key={`m${k}`} data-testid={`${mk.kind === "working" ? "focus-target" : "focus-main-point"}${suffix}`} className={markClass(mk.kind, visible)}>{text.slice(mk.start, mk.end)}</mark>);
    if (k === ms.length - 1) acc.push(<span key="tail">{text.slice(mk.end)}</span>);
    return acc;
  }, []);
  // FORMATTED PATH: the same marks wrapped onto the text nodes of the student's formatting (derived display only — the stored formatting and the
  // editor's contentEditable are never touched); a mark with no safe mapping is left undrawn, the formatting stands
  const markWrapper = (suffix, visible) => (mk, k) => {
    const el = document.createElement("mark");
    el.className = markClass(mk.kind, visible);
    el.setAttribute("data-kind", mk.kind);
    el.setAttribute("data-testid", `${mk.kind === "working" ? "focus-target" : "focus-main-point"}${suffix}${k === 0 ? "" : "-cont"}`);
    return el;
  };
  const annotatedHtml = (html, text, ms, suffix, visible) => {
    const r = annotateMarks(html, text, ms, markWrapper(suffix, visible));
    return r ? r.html : null;
  };
  // THE MIRROR: the editor's own sanitized HTML (identical metrics, transparent text) carrying the marks — always the formatted path, because
  // the mirror's HTML is by construction a valid display of the draft; if not (never expected), the plain text keeps the layout honest
  const mirrorHtml = useMemo(() => (marks.length ? annotatedHtml(liveHtml, draft, marks, "", false) : null) || sanitizeFormatting(liveHtml), [liveHtml, draft, marks]);   // eslint-disable-line react-hooks/exhaustive-deps

  const saveNow = () => ((fmt || openedFmt) ? onSave(draft.trim(), fmt || "") : onSave(draft.trim()));   // plain writing saves exactly as before
  const leave = async (go) => {
    if (leaving) return;
    setLeaving(true);
    try {
      if (changed && draft.trim()) await saveNow();
      clearDraft(draftScope, unit.id);
      await go();
    } catch { /* the save failed: the words stay in the editor (and in the draft store) */ }
    finally { setLeaving(false); }
  };
  const submit = () => {
    if (!draft.trim() || pending) return;
    clearDraft(draftScope, unit.id);
    // the turn carries the words (the unit is written first — unchanged) and, beside them, the student's formatting; the engine never reads it
    if (fmt || openedFmt) onTurn(draft.trim(), textChanged ? "revise" : "writing", fmt || "");
    else onTurn(draft.trim(), textChanged ? "revise" : "writing");
    setTouched(false);
  };

  const mirror = (
    <div ref={backRef} aria-hidden data-testid="focus-highlight-layer"
      className={`writing-editor focus-mirror pointer-events-none absolute inset-0 overflow-hidden ${EDITOR_TEXT_CLASS} text-transparent`}
      dangerouslySetInnerHTML={{ __html: mirrorHtml }} />
  );

  return (
    <section data-testid="paragraph-focus" data-unit={unit.id} data-span-paragraphs={parts.length} data-span-expected={span ? span.expected : 1}
      data-span-mismatch={spanOk ? undefined : "true"} className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#8C3A2A]">Working on this part</p>
          {location ? <p data-testid="focus-location" className="mt-0.5 text-[12.5px] text-stone-500">{location}</p> : null}
        </div>
        <button type="button" data-testid="focus-back" disabled={leaving} onClick={() => leave(onBack)} className={`${NAV} font-medium`}>
          <CornerUpLeft className="h-3.5 w-3.5" /> Go back
        </button>
      </div>

      <div data-testid="focus-span" className="space-y-2">
        {!spanOk ? (
          <p data-testid="focus-span-mismatch" className="text-[11.5px] italic text-stone-500">
            Part of this part of your writing isn't shown here yet — what you see below is not the whole part.
          </p>
        ) : null}
        {parts.map((p, i) => (i === editorAt ? (
          <div key={p.id} className="relative" data-testid="focus-editor">
            <WritingEditor key={gen} testid="focus-text" initialText={draft} initialFormatting={openedFmt} autoFocus={false} minHeightPx={220}
              onChange={change} onSave={submit}
              onScroll={(e) => { if (backRef.current) backRef.current.scrollTop = e.target.scrollTop; }}
              backdrop={mirror}
              placeholder="Shape this part of your writing for your reader…" />
            {(marks.length || Object.keys(contextMarks).length) ? (
              <p data-testid="focus-highlight-key" className="mt-1 flex items-center gap-4 text-[10.5px] text-stone-500">
                {anyMain ? <span className="inline-flex items-center gap-1.5"><span aria-hidden className="inline-block h-2.5 w-2.5 rounded-[2px] bg-sky-300/[0.7]" />Main point</span> : null}
                {anyWorking ? <span className="inline-flex items-center gap-1.5"><span aria-hidden className="inline-block h-2.5 w-2.5 rounded-[2px] bg-amber-300/[0.8]" />Working here</span> : null}
              </p>
            ) : null}
          </div>
        ) : (() => {
          // READ-ONLY CONTEXT PARAGRAPH: its valid formatting renders (with the marks wrapped onto it); plain prose keeps the plain path
          const pf = validFormatting(p.text, p.formatting);
          const ms = contextMarks[p.id] || [];
          const html = pf ? (ms.length ? annotatedHtml(pf, p.text, ms, `-ctx-${p.id}`, true) : pf) : null;
          return (
            <p key={p.id} data-testid={`focus-span-part-${p.id}`} data-context-part="true" data-formatted={html ? "true" : undefined}
              className={`whitespace-pre-line rounded-md border border-dashed border-stone-200 bg-stone-50/60 ${PROSE} text-stone-700`}>
              {html ? <span className="writing-formatted" dangerouslySetInnerHTML={{ __html: html }} /> : (ms.length ? renderMarked(p.text, ms, `-ctx-${p.id}`, true) : p.text)}
            </p>
          );
        })()))}
        {anyWorking ? <p data-testid="focus-target-note" className="text-[11px] text-stone-400">Highlighted: the writing we're talking about right now.</p> : null}
      </div>

      {/* THE ACTION ROW: one read action (primary) · Move on (secondary) */}
      <div data-testid="focus-actions" className="flex flex-wrap items-center gap-3">
        <button type="button" data-testid="focus-submit" onClick={submit} disabled={pending || !draft.trim()}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#8C3A2A] text-white text-[12px] font-medium px-3.5 py-1.5 hover:bg-[#6B2C20] disabled:opacity-40 transition-colors">
          {pending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
          {pending ? "TWS is reading this part…" : (changed ? "Save & read with TWS" : "Read with TWS")}
        </button>
        {onMoveOn ? (
          <button type="button" data-testid="focus-move-on" disabled={leaving} onClick={() => leave(onMoveOn)} data-pending={leaving ? "1" : undefined}
            title="I'm finished working on this part for now — continue. The Writing Studio decides what comes next."
            className="inline-flex items-center gap-1 rounded-full border border-stone-300 bg-white px-3 py-1.5 text-[12px] font-medium text-stone-600 hover:border-[#8C3A2A] hover:text-[#8C3A2A] transition-colors disabled:opacity-60">
            {leaving ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Working…</> : <>Move on <ArrowRight className="h-3.5 w-3.5" /></>}
          </button>
        ) : null}
        {changed && !pending ? <span data-testid="focus-unsaved" className="text-[11px] text-stone-400">Your changes are kept when you leave.</span> : null}
      </div>

    </section>
  );
}
